//list of supported currencies from PRD
export const SUPPORTED_CURRENCIES = [
  'USD' as const,
  'CAD' as const,
  'EUR' as const,
  'GBP' as const,
  'AUD' as const,
  'JPY' as const,
  'CNY' as const,
  'SGD' as const,
  'HKD' as const,
  'NZD' as const,
  'SEK' as const,
  'NOK' as const,
  'DKK' as const,
  'CHF' as const,
  'ZAR' as const,
  'BRL' as const,
  'INR' as const,
  'KRW' as const,
  'MXN' as const,
  'TWD' as const,
];

export type CurrencyCode = (typeof SUPPORTED_CURRENCIES)[number];

//get currency symbol from currency code
export const getCurrencySymbol = (currencyCode: CurrencyCode, fullSymbol = true) => {
  const currencySymbol = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
  })
    .formatToParts(0)
    .find((part) => part.type === 'currency')!.value;

  return fullSymbol ? `${currencySymbol} (${currencyCode})` : currencySymbol;
};

export const currencyFormatter = (
  val: number,
  currency: CurrencyCode = 'USD',
  locale = 'en-US',
  symbolProperties: { showCurrencySymbol: boolean; fullSymbol?: boolean } = {
    showCurrencySymbol: true,
    fullSymbol: false,
  },
) => {
  // this is passed as null not undefined
  // so it is not defaulted to USD
  currency = (currency ?? 'USD').toUpperCase() as CurrencyCode;
  if (isNaN(val)) {
    throw new Error('Invalid value provided for currency formatting');
  }
  const { showCurrencySymbol, fullSymbol } = symbolProperties;
  // create a formatter, adjusting style based on whether we show the symbol
  const formatter = new Intl.NumberFormat(locale, {
    style: showCurrencySymbol ? 'currency' : 'decimal', // use currency if showing symbols
    currency, // ensure currency code is uppercase
  });

  let formattedValue = formatter.format(val);
  // if we are showing the currency symbol and want a full symbol, replace it
  if (showCurrencySymbol && fullSymbol) {
    const currencySymbol = getCurrencySymbol(currency, true); // full symbol (with currency code)
    const shortCurrencySymbol = getCurrencySymbol(currency, false); // just the symbol
    formattedValue = formattedValue.replace(
      shortCurrencySymbol,
      currencySymbol + ' ', // add a space after the full symbol for better readability
    );
  }

  return formattedValue;
};

export const printLabelsCurrencyPriority = ['USD', 'MXN', 'CAD'];

// priority sort, then string sort
export function prioritySort<T>(priorityList: string[]) {
  return (a: string & T, b: string & T) => {
    const aPriority = priorityList.indexOf(a);
    const bPriority = priorityList.indexOf(b);

    if (aPriority > -1 && bPriority > -1) {
      return aPriority - bPriority;
    }

    if (aPriority > -1) return -1;
    if (bPriority > -1) return 1;

    return a.localeCompare(b);
  };
}
